import {
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  getDoc,
  getDocs,
  QuerySnapshot,
  setDoc,
  SetOptions,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../firebase";
import { getTimestampForTPlus } from "../lib/get-timestamp-for-t-plus";
import { User } from "../types/User";
import { debugLogError } from "../utils/debug-log-error";

/**
 * Fetches all users from the Firestore 'users' collection.
 * Note: This function itself doesn't enforce admin-only access.
 * Access control should be handled by the calling component or route.
 * @returns A promise that resolves to an array of User objects.
 * @throws Will throw an error if fetching users fails.
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const usersCollection = collection(firestore, "users");
    const querySnapshot: QuerySnapshot<DocumentData> =
      await getDocs(usersCollection);

    const users: User[] = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: doc.id,
        email: data.email || null,
        createdAt: data.createdAt || null,
        onboardingCompleted: data.onboardingCompleted || false,
        usernameUpdateCount: data.usernameUpdateCount || 0,
        nextUsernameUpdateAt:
          data.nextUsernameUpdateAt || getTimestampForTPlus(1),
        blockedUsers: data.blockedUsers || [],
      };
    });
    return users;
  } catch (error) {
    debugLogError("getAllUsers", error);
    throw new Error("Failed to fetch users.");
  }
};

/**
 * Fetches a single user document from Firestore by UID.
 * This is a helper function and does not map to the User type.
 * @param uid The user's unique ID.
 * @returns A promise that resolves to the DocumentSnapshot.
 * @throws Will throw an error if fetching the document fails.
 */
const getUserDoc = async (
  uid: string
): Promise<DocumentSnapshot<DocumentData>> => {
  try {
    const userDocRef = doc(firestore, "users", uid);
    const docSnap = await getDoc(userDocRef);
    return docSnap;
  } catch (error) {
    debugLogError("getUserDoc", error);
    throw new Error(`Failed to fetch user document for UID: ${uid}.`);
  }
};

/**
 * Fetches a single user from the Firestore 'users' collection by UID.
 * Access control should be handled by the calling component or route.
 * @param uid The user's unique ID.
 * @returns A promise that resolves to a User object, or null if not found.
 * @throws Will throw an error if fetching the user fails.
 */
export const getUserById = async (uid: string): Promise<User | null> => {
  try {
    const docSnap = await getUserDoc(uid);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        uid: docSnap.id,
        email: data.email || null,
        createdAt: data.createdAt || null,
        onboardingCompleted: data.onboardingCompleted || false,
        usernameUpdateCount: data.usernameUpdateCount || 0,
        nextUsernameUpdateAt:
          data.nextUsernameUpdateAt || getTimestampForTPlus(1),
        blockedUsers: data.blockedUsers || [],
      };
    } else {
      return null;
    }
  } catch (error) {
    debugLogError("getUserById", error);
    throw new Error(`Failed to fetch user with UID: ${uid}.`);
  }
};

/**
 * Calls the 'resetOnboardingStatus' cloud function to reset a user's onboarding status.
 * @param uid The user ID whose onboarding status is to be reset.
 * @returns A promise that resolves when the operation is complete.
 * @throws Will throw an error if calling the cloud function fails.
 */
export const resetUserOnboarding = async (uid: string): Promise<void> => {
  const functions = getFunctions();
  const resetOnboardingStatusCallable = httpsCallable(
    functions,
    "resetOnboardingStatus"
  );

  try {
    console.log(`Calling resetOnboardingStatus for UID: ${uid}`);
    const result = await resetOnboardingStatusCallable({ uid });
    console.log(
      "resetOnboardingStatus cloud function called successfully",
      result
    );
  } catch (error) {
    console.error(
      `Error calling resetOnboardingStatus cloud function for UID: ${uid}`,
      error
    );
    throw new Error(`Failed to reset onboarding status for user ${uid}.`);
  }
};

export async function updateUserOnboardingStatus(
  userUid: string,
  status: boolean
) {
  const userDocRef = doc(firestore, "users", userUid);
  await setDoc(userDocRef, { onboardingCompleted: status }, {
    merge: true,
  } as SetOptions);
}
